import lscache from 'lscache';
import { PMCConfig, ThirdPartyConfig } from '@kyruus/types';

/**
 * Client-side helpers for the signed patient token integration: the customer's portal launches PMC
 * with a JWT identifying the patient on the URL, which we post to /api/patient-lookup/signed-token.
 * Configured on the customer config at `third_party.signed_patient_token`.
 */

// query param the token is read from when the config doesn't set `query_param`
export const DEFAULT_SIGNED_PATIENT_TOKEN_QUERY_PARAM = 'bcs_token';

// local storage key the token is persisted under
export const SIGNED_PATIENT_TOKEN_STORAGE_KEY = 'signed_patient_token';

// length of time in minutes to persist the token to local storage
const SIGNED_PATIENT_TOKEN_EXPIRATION = 60;

type SignedPatientTokenConfig = {
  public_key?: string;
  query_param?: string;
};

// TODO: drop the cast once `signed_patient_token` is added to ThirdPartyConfig in @kyruus/types
function getSignedPatientTokenConfig(
  config: PMCConfig | null | undefined
): SignedPatientTokenConfig | undefined {
  return (
    config?.third_party as
      | (ThirdPartyConfig & { signed_patient_token?: SignedPatientTokenConfig })
      | undefined
  )?.signed_patient_token;
}

// is the signed patient token integration configured for this customer?
export function isSignedPatientTokenEnabled(
  config: PMCConfig | null | undefined
): boolean {
  return Boolean(getSignedPatientTokenConfig(config)?.public_key);
}

export function getSignedPatientTokenQueryParam(
  config: PMCConfig | null | undefined
): string {
  return (
    getSignedPatientTokenConfig(config)?.query_param ||
    DEFAULT_SIGNED_PATIENT_TOKEN_QUERY_PARAM
  );
}

export function getSignedPatientTokenFromUrl(
  config: PMCConfig | null | undefined
): string | null {
  // disable check server-side
  if (typeof window === 'undefined') {
    return null;
  }
  return new URLSearchParams(window.location.search).get(
    getSignedPatientTokenQueryParam(config)
  );
}

export function getSignedPatientTokenFromLocalStorage(): string | null {
  return lscache.get(SIGNED_PATIENT_TOKEN_STORAGE_KEY);
}

/**
 * Get the signed patient token from the configured URL param, falling back to a token persisted
 * to local storage on an earlier page. Returns null when the integration is not configured.
 */
export function getSignedPatientToken(
  config: PMCConfig | null | undefined
): string | null {
  if (!isSignedPatientTokenEnabled(config)) {
    return null;
  }
  return (
    getSignedPatientTokenFromUrl(config) ??
    getSignedPatientTokenFromLocalStorage()
  );
}

/**
 * Persists a token found on the URL into localStorage (via lscache). Needed because client-side
 * route changes (e.g. to /book) drop the query param.
 */
export function persistSignedPatientToken(
  config: PMCConfig | null | undefined
): void {
  if (!isSignedPatientTokenEnabled(config)) {
    return;
  }

  const token = getSignedPatientTokenFromUrl(config);
  if (token) {
    lscache.set(
      SIGNED_PATIENT_TOKEN_STORAGE_KEY,
      token,
      SIGNED_PATIENT_TOKEN_EXPIRATION
    );
  }
}
